import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { createGlobalStyle } from 'styled-components';
import { useTranslation, I18nextProvider } from 'react-i18next';
import i18n from './i18n'; // Import i18n instance
import { store } from './store';
import AppRouter from './navigation/AppRouter';
import ErrorBoundary from './components/ErrorBoundary';
import { Colors, FontSizes, FontWeights, BorderRadius, Spacing } from './constants/theme';
import { srOnlyStyles } from './utils/accessibility';
import './styles/rtl.css'; // RTL layout support

const GlobalStyle = createGlobalStyle`
  /* Corporate Font Import */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  /* Screen Reader Only Styles */
  ${srOnlyStyles}
  
  /* Accessibility Focus Styles */
  *:focus {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: currentColor !important;
    }
  }
  
  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    background-color: ${Colors.background};
    color: ${Colors.textPrimary};
    line-height: 1.6;
    font-size: ${FontSizes.md}px;
    font-weight: ${FontWeights.normal};
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    letter-spacing: -0.005em;
  }

  #root {
    height: 100vh;
    overflow: hidden;
    background-color: ${Colors.background};
  }

  /* Ultra-Modern Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-weight: ${FontWeights.semibold};
    color: ${Colors.textPrimary};
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin: 0;
  }
  
  h1 { font-size: ${FontSizes.hero}px; font-weight: ${FontWeights.bold}; }
  h2 { font-size: ${FontSizes.display}px; }
  h3 { font-size: ${FontSizes.xxxl}px; }
  h4 { font-size: ${FontSizes.xxl}px; }
  h5 { font-size: ${FontSizes.xl}px; }
  h6 { font-size: ${FontSizes.lg}px; }

  button {
    font-family: inherit;
    border: none;
    cursor: pointer;
    transition: all 150ms ease;
    font-weight: ${FontWeights.medium};
    
    &:focus {
      outline: none;
    }
  }

  input, textarea, select {
    font-family: inherit;
    border: 1px solid ${Colors.border};
    border-radius: ${BorderRadius.sm}px;
    padding: ${Spacing.sm}px ${Spacing.md}px;
    font-size: ${FontSizes.md}px;
    background-color: ${Colors.surface};
    color: ${Colors.textPrimary};
    transition: all 150ms ease;
    
    &:focus {
      outline: none;
      border-color: ${Colors.borderFocus};
      box-shadow: 0 0 0 3px ${Colors.borderFocus}20;
    }
    
    &:hover {
      border-color: ${Colors.borderDark};
    }
  }

  /* Power BI Scrollbar styling */
  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }

  ::-webkit-scrollbar-track {
    background: ${Colors.backgroundDark};
    border-radius: 6px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${Colors.borderDark};
    border-radius: 6px;
    border: 2px solid ${Colors.backgroundDark};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${Colors.textTertiary};
  }

  ::-webkit-scrollbar-corner {
    background: ${Colors.backgroundDark};
  }

  /* Power BI Focus styles */
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid ${Colors.primary};
    outline-offset: 2px;
  }

  /* Power BI Selection styles */
  ::selection {
    background-color: ${Colors.surfaceSelected};
    color: ${Colors.textPrimary};
  }

  /* Power BI Animation defaults */
  * {
    transition-timing-function: cubic-bezier(0.4, 0.0, 0.2, 1);
  }

  /* Disable outline for mouse users */
  .js-focus-visible :focus:not(.focus-visible) {
    outline: none;
  }
`;

const AppContent: React.FC = () => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Apply RTL class for Arabic
    const isRTL = i18n.language === 'ar';
    if (isRTL) {
      document.body.classList.add('rtl');
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.body.classList.remove('rtl');
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', i18n.language);
    }
  }, [i18n.language]);
  
  return (
    <>
      <GlobalStyle />
      <AppRouter />
    </>
  );
};

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </Provider>
    </I18nextProvider>
  );
}

export default App;
